import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { FC, useEffect } from 'react';
import {
  Challenge,
  ContentType,
  GeneratedPlan,
  GeneratedScript,
} from 'src/api/generatedContent';
import { useAIAssistantContext } from 'src/hooks/AIAssistant/useAiAssistantContext';
import { postChallengeToScriptParams } from 'src/utils/aiGeneratedContent';
import styles from './AIContentCreator.module.scss';
import CirclePlanForm from './CirclePlanForm';
import CirclePlanChallenges from './CirclePlanIdeas';
import ScriptContentForm from './ScriptContentForm';
import VideoScript from './VideoScript';

interface AIContentCreatorProps {
  goToContentLibrary?: () => void;
}

export const AIContentCreator: FC<AIContentCreatorProps> = ({
  goToContentLibrary,
}) => {
  const {
    contentType,
    setContentType,
    prefilledScriptData,
    setPrefilledScriptData,
    script,
    setScript,
    isContentGenerated,
    setIsContentGenerated,
    circlePlan,
    setCirclePlan,
    selectedChallengeIndex,
    setSelectedChallengeIndex,
    resetFormStates,
    switchToTab,
    activeTab,
  } = useAIAssistantContext();

  useEffect(() => {
    if (activeTab === 0) {
      if (!circlePlan && !script && isContentGenerated) {
        setIsContentGenerated(false);
      }
    }
  }, [
    activeTab,
    circlePlan,
    script,
    isContentGenerated,
    setIsContentGenerated,
  ]);

  const handleContentTypeChange = (event: SelectChangeEvent<ContentType>) => {
    const newContentType = event.target.value as ContentType;
    setContentType(newContentType);
    setIsContentGenerated(false);
    setCirclePlan(null);
    setScript(null);
    setPrefilledScriptData(null);
  };

  const handlePlanGenerated = (plan: GeneratedPlan) => {
    setCirclePlan(plan);
    setScript(null);
    setContentType(ContentType.CIRCLE_PLAN);
    setIsContentGenerated(true);
  };

  const handleScriptGenerated = (script: GeneratedScript) => {
    setScript(script);
    setCirclePlan(null);
    setContentType(ContentType.SCRIPT);
    setIsContentGenerated(true);
  };

  const handleSelectChallenge = (index: number) => {
    setSelectedChallengeIndex(index);
  };

  const handleDevelopChallenge = (challenge: Challenge, planId: string) => {
    const scriptParams = postChallengeToScriptParams(challenge);
    resetFormStates();
    setContentType(ContentType.SCRIPT);
    setPrefilledScriptData({
      ...scriptParams,
      planId: planId,
      challengeId: challenge._id,
    });

    switchToTab(0);
  };

  const resetForm = () => {
    resetFormStates();
    setPrefilledScriptData(null);
  };

  return (
    <div className={styles.container}>
      {!isContentGenerated && (
        <FormControl fullWidth>
          <InputLabel id="content-type-label">Content Type</InputLabel>
          <Select
            labelId="content-type-label"
            id="content-type-select"
            value={contentType}
            label="Content Type"
            onChange={handleContentTypeChange}
          >
            <MenuItem value={ContentType.CIRCLE_PLAN}>Circle Plan</MenuItem>
            <MenuItem value={ContentType.SCRIPT}>Video Script</MenuItem>
          </Select>
        </FormControl>
      )}
      {!isContentGenerated && (
        <>
          {contentType === ContentType.CIRCLE_PLAN && (
            <div id="circle-plan-form">
              <CirclePlanForm onPlanGenerated={handlePlanGenerated} />
            </div>
          )}

          {contentType === ContentType.SCRIPT && (
            <div id="coaching-form">
              <ScriptContentForm
                onContentGenerated={handleScriptGenerated}
                initialData={prefilledScriptData ?? null}
              />
            </div>
          )}
        </>
      )}

      {isContentGenerated && (
        <>
          {contentType === ContentType.CIRCLE_PLAN && circlePlan && (
            <CirclePlanChallenges
              circlePlan={circlePlan}
              selectedChallengeIndex={selectedChallengeIndex}
              onSelectChallenge={handleSelectChallenge}
              onDevelopChallenge={(challenge) =>
                handleDevelopChallenge(challenge, circlePlan._id)
              }
              onPlanUpdated={(updatedPlan) => setCirclePlan(updatedPlan)}
            />
          )}

          {contentType === ContentType.SCRIPT && script && (
            <VideoScript script={script} />
          )}

          <div className={styles.actionButtons}>
            <Button variant="outlined" color="primary" onClick={resetForm}>
              Start Over
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={goToContentLibrary}
            >
              Go to Content Library
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AIContentCreator;
